import { Optional } from '@omni/kit/Types';
import {
  KitButton,
  KitInternationalPhoneNumberInput,
  KitSnack,
  KitText,
  KitTouchable,
  KitVerifyCodeInput,
} from '@omni/kit/components';
import { createMaskFromFormat } from '@omni/kit/components/KitInternationalPhoneNumberInput/components/KitInternationalPhoneNumberInput';
import countries from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/countries.json';
import { Country } from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/types';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitSnackRender from '@omni/kit/components/KitSnackRender';
import Show from '@omni/kit/components/Show';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useCountdownTimer } from '@omni/kit/hooks/useCountdownTimer';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService, { IHttpResponse } from '@omni/kit/services/BaseService';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { notifyAppUserSettingsUpdated } from '@omni/kit/utilities/NativeHelpers';
import sanitizePhoneNumber from '@omni/kit/utilities/sanitizePhoneNumber';
import {
  capitalizeFirstLetter,
  handleHttpErrorResponse,
} from '@omni/kit/utilities/utilities';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Keyboard, Platform, StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { formatWithMask } from 'react-native-mask-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useBlockPageContext } from '../../../../BlockPageContext';
import { UpdateActionInterface } from '../../../types';

export interface ChangePhoneNumberModalProps {
  appKey: string;
  clientBrandColor?: string;
  phoneNumber: string;
  profileID?: string;
  setRefreshing?: (refreshing: boolean) => void;
  setVisible: (visible: boolean) => void;
  /** The action to perform when a user changes their phone number. */
  updatePhoneAction?: UpdateActionInterface;
  visible: boolean;
  phoneNumberObj: {
    significant: string;
    country: { region_code: string; calling_code: string };
  };
  verifiedPhoneNumber?: {
    significant: string;
    country: { region_code: string; calling_code: string };
  };
}

const ChangePhoneNumberModal: React.FC<ChangePhoneNumberModalProps> = ({
  appKey,
  phoneNumber,
  profileID,
  setRefreshing,
  setVisible,
  updatePhoneAction,
  visible,
  phoneNumberObj,
  verifiedPhoneNumber,
}): ReactElement => {
  const { t } = useTranslation(['blocks']);

  const { tokens } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();

  const { sizeClassV2 } = useSizeClass();
  const { bottom: insetsBottom } = useSafeAreaInsets();
  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );

  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.l,
  });

  const [step, setStep] = useState<number>(1);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');
  const [country, setCountry] = useState<Country | undefined>(undefined);
  const {
    counter,
    isActive: isResendCodeDisabled,
    start: startCountdown,
    stop: stopCountdown,
  } = useCountdownTimer();

  const { masked: formattedPhoneNumber } = formatWithMask({
    text: newPhoneNumber,
    mask: createMaskFromFormat(country?.format || 'XXXXXXXXXX'),
  });

  const isSaveButtonDisabled = useMemo((): boolean => {
    if (newPhoneNumber.length === 0) {
      return false;
    }

    if (
      verifiedPhoneNumber?.significant &&
      phoneNumberObj?.significant === newPhoneNumber
    ) {
      return true;
    }

    return (country && newPhoneNumber.length !== country?.length) || isLoading;
  }, [newPhoneNumber, country, verifiedPhoneNumber, phoneNumberObj, isLoading]);

  const getFormattedPhoneNumber = useMemo((): string => {
    const newLine = isMobileDevice ? '\n' : '';

    return `${newLine}${country?.callingCode} ${formattedPhoneNumber}`;
  }, [country, formattedPhoneNumber, isMobileDevice]);

  const showKitSnack = (message: string, duration: number): void => {
    setSnackOptions({
      duration,
      marginBottom: Spacing.l,
      message,
      visible: true,
    });
  };

  const makeRequestForChangePhoneNumber = async (): Promise<IHttpResponse> => {
    // make a request with feeds endpoint to update phone number
    const originalAction = updatePhoneAction;

    if (originalAction?.updateUrl && originalAction?.settings?.[0]) {
      const settings = [...(originalAction?.settings ?? [{}])];
      settings[0] = {
        ...settings[0],
        value: {
          significant: sanitizePhoneNumber(newPhoneNumber),
          country: {
            region_code: newPhoneNumber ? country?.regionCode : '',
            calling_code: newPhoneNumber ? country?.callingCode : '',
          },
        },
      };

      const url = originalAction.updateUrl;

      return await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { profileID, settings },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } else {
      throw new Error();
    }
  };

  const saveNewPhoneNumber = async (): Promise<void> => {
    if (!country && newPhoneNumber) {
      setErrorMessage(t('blocks:changePhoneNumber.error.countryRequired'));
      setShowError(true);

      return;
    }

    setIsLoading(true);

    const res = await makeRequestForChangePhoneNumber();

    const ok = res.status === 201 || res.status === 200;

    const hasPromptPhoneOtp = res.headers?.['prompt-phone-otp'] === 'true';

    if (ok) {
      if (hasPromptPhoneOtp) {
        setStep(2);
      } else {
        // Server takes a while before returning the updated data
        onClose();
        setRefreshing?.(true); // auto-refresh the entire screen to display new phone number if provided by backend
        notifyAppUserSettingsUpdated();
        KitSnack.show(t('blocks:changePhoneNumber.phoneNumberUpdated'));
      }
    } else {
      const { status, message } = await handleHttpErrorResponse(res);

      if (status === 400) {
        setErrorMessage(
          t('blocks:changePhoneNumber.error.enterValidPhoneNumber')
        );
        setShowError(true);
      } else if (status === 409) {
        setErrorMessage(t('blocks:changePhoneNumber.error.phoneNumberExists'));
        setShowError(true);
      } else {
        if (isMobileDevice) {
          showKitSnack(
            message?.length
              ? capitalizeFirstLetter(message)
              : t('blocks:changePhoneNumber.phoneNumberUpdateFailed'),
            KitSnackDuration.SHORT
          );
        } else {
          Keyboard.dismiss();
          KitSnack.show(
            message?.length
              ? capitalizeFirstLetter(message)
              : t('blocks:changePhoneNumber.phoneNumberUpdateFailed')
          );
        }
      }
    }

    setIsLoading(false);
  };

  const handleVerificationCode = (
    success = true,
    error: Optional<string> = null
  ): void => {
    /**
     *  Based on phone-number-verification-code request we decide what interactions should
     *  happen for the user regarding the error type (verificationCodeInvalid, codeExpired or tooManyAttempts)
     */
    if (success) {
      // Server takes a while before returning the updated data
      onClose();
      setRefreshing?.(true); // auto-refresh the entire screen to display new phone number if provided by backend
      notifyAppUserSettingsUpdated();
      KitSnack.show(t('blocks:changePhoneNumber.phoneNumberVerified'));
    } else {
      if (error === 'verification_code_invalid') {
        showKitSnack(
          t('blocks:changePhoneNumber.error.invalid'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_expired') {
        showKitSnack(
          t('blocks:changePhoneNumber.error.expired'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_too_many_attempts') {
        KitSnack.show(t('blocks:changePhoneNumber.error.tooManyAttempts'));
      } else if (error === 'phone_number_already_exists') {
        KitSnack.show(t('blocks:changePhoneNumber.error.phoneNumberExists'));
      } else {
        if (isMobileDevice) {
          showKitSnack(
            t('blocks:changePhoneNumber.error.default'),
            KitSnackDuration.SHORT
          );
        } else {
          Keyboard.dismiss();
          KitSnack.show(t('blocks:changePhoneNumber.error.default'));
        }
      }
    }
  };

  const confirmVerificationCode = async (): Promise<void> => {
    setIsVerifying(true);

    const originalAction = updatePhoneAction;

    if (originalAction?.confirmPhoneUrl) {
      const url = originalAction.confirmPhoneUrl;

      const res = await BaseService.Post({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: {
          code: verificationCode,
          phone: {
            significant: sanitizePhoneNumber(newPhoneNumber),
            country: {
              calling_code: newPhoneNumber ? country?.callingCode : '',
              region_code: newPhoneNumber ? country?.regionCode : '',
            },
          },
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const ok = res.status === 201 || res.status === 200;

      if (ok) {
        handleVerificationCode();
      } else {
        const { status, data } = await handleHttpErrorResponse(res);

        if (status === 403) {
          setErrorMessage(t('blocks:changePhoneNumber.error.invalid'));
          setShowError(true);
        } else {
          handleVerificationCode(false, data?.error);
        }
      }
    }

    setIsVerifying(false);
  };

  const resendCode = async (): Promise<void> => {
    setVerificationCode('');

    const res = await makeRequestForChangePhoneNumber();

    const ok = res.status === 201 || res.status === 200;

    startCountdown();

    if (!ok) {
      const { message } = await handleHttpErrorResponse(res);

      if (isMobileDevice) {
        showKitSnack(
          message ?? t('blocks:changePhoneNumber.newCodeSendFailed'),
          KitSnackDuration.SHORT
        );
      } else {
        Keyboard.dismiss();
        KitSnack.show(
          message ?? t('blocks:changePhoneNumber.newCodeSendFailed')
        );
      }
    }
  };

  const onClose = () => {
    if (step === 2) {
      setRefreshing?.(true); // auto-refresh the entire screen to display new phone number if provided by backend
      notifyAppUserSettingsUpdated();
    }

    setVisible(false);
    setNewPhoneNumber('');
    setCountry(undefined);
    setShowError(false);
    setErrorMessage('');
    setVerificationCode('');
    stopCountdown();
  };

  const renderContent = () => (
    <View
      style={{
        ...styles.container,
        ...(isMobileDevice
          ? { paddingTop: Spacing.xxl }
          : { paddingTop: Spacing.xl }),
      }}
    >
      <KitText
        extraBold
        fontSize={24}
        lineHeight={28}
        fontWeight={900}
        color={Colors.N900}
      >
        {step === 1
          ? t('blocks:changePhoneNumber.updatePhoneNumber')
          : t('blocks:changePhoneNumber.verifyCodeNumber')}
      </KitText>
      <View style={styles.contentContainer}>
        {step === 1 ? (
          <>
            <KitInternationalPhoneNumberInput
              autoFocus={true}
              label={t('blocks:changePhoneNumber.mobilePhoneNumber')}
              onChangeText={(text) => {
                setNewPhoneNumber(sanitizePhoneNumber(text));
                setShowError(false);
              }}
              value={newPhoneNumber}
              selectedCountry={country}
              onCountryChange={(country) => {
                setCountry(country);
                if (newPhoneNumber.length > country.length) {
                  setShowError(true);
                  setErrorMessage(
                    t('blocks:changePhoneNumber.error.confirmNumber')
                  );
                  setNewPhoneNumber(newPhoneNumber.slice(0, country.length));
                } else {
                  setShowError(false);
                  setNewPhoneNumber(newPhoneNumber);
                }
              }}
              placeholderTextColor={Colors.N200}
              countries={countries}
              returnKeyType='go'
              blurOnSubmit
              onSubmitEditing={() => {
                if (!isSaveButtonDisabled) {
                  saveNewPhoneNumber();
                }
              }}
              style={{ marginBottom: Spacing.xl }}
            />
            <Show show={newPhoneNumber.length === 0 ? false : showError}>
              <View style={styles.errorContainer}>
                <KitText color={Colors.N0} fontSize={16}>
                  {errorMessage}
                </KitText>
              </View>
            </Show>
            <View
              style={{
                gap: Spacing.m,
                ...(!isMobileDevice && {
                  flexDirection: 'row-reverse',
                }),
              }}
            >
              <KitButton
                color={Colors.N1000}
                disabled={isSaveButtonDisabled}
                onPress={saveNewPhoneNumber}
                isLoading={isLoading}
                title={t('blocks:changePhoneNumber.continue')}
              />
              <KitButton secondary onPress={onClose} title='Cancel' />
            </View>
          </>
        ) : (
          <>
            {country?.callingCode ? (
              <KitText
                color={Colors.N900}
                style={{
                  marginBottom: Spacing.l,
                }}
              >
                {t('blocks:changePhoneNumber.enterCodeNumber', {
                  phoneNumber: getFormattedPhoneNumber,
                })}
              </KitText>
            ) : null}

            <KitVerifyCodeInput
              setValue={(code) => {
                setVerificationCode(code);
                setShowError(false);
              }}
              value={verificationCode}
              returnKeyType='go'
              blurOnSubmit
              numeric
              onSubmitEditing={() => {
                if (!isVerifying && verificationCode.length === 6) {
                  confirmVerificationCode();
                }
              }}
              onAutoSubmit={confirmVerificationCode}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  marginBottom: Spacing.l,
                  marginTop: Spacing.m,
                  ...(!isMobileDevice && {
                    marginRight: '22%',
                  }),
                }}
              >
                {!isResendCodeDisabled ? (
                  <KitTouchable onPress={resendCode} noRadius>
                    <KitText
                      color={Colors.N500}
                      style={{
                        textDecorationLine: 'underline',
                      }}
                    >
                      {t('blocks:changePhoneNumber.resendCodeShort')}
                    </KitText>
                  </KitTouchable>
                ) : (
                  <KitText color={Colors.N300} fontSize={16}>
                    {t('blocks:changePhoneNumber.requestNewCodeIn', {
                      seconds: counter,
                    })}
                  </KitText>
                )}
              </View>
              <Show show={showError}>
                <View style={styles.errorContainer}>
                  <KitText color={Colors.N0} fontSize={16}>
                    {errorMessage}
                  </KitText>
                </View>
              </Show>
              <View
                style={{
                  gap: Spacing.m,
                  ...(!isMobileDevice && {
                    flexDirection: 'row-reverse',
                  }),
                }}
              >
                <KitButton
                  color={Colors.N1000}
                  disabled={
                    isVerifying || verificationCode.length !== 6 || showError
                  }
                  onPress={confirmVerificationCode}
                  isLoading={isVerifying}
                  title={t('blocks:changePhoneNumber.continue')}
                />
                <KitButton secondary onPress={onClose} title='Cancel' />
              </View>
            </KitVerifyCodeInput>
          </>
        )}
      </View>
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
    </View>
  );

  useEffect((): void => {
    if (
      visible &&
      phoneNumberObj?.country?.region_code &&
      phoneNumberObj?.significant
    ) {
      setNewPhoneNumber(phoneNumberObj?.significant);
      setCountry(
        countries.find(
          (country: Country) =>
            country.regionCode === phoneNumberObj?.country?.region_code
        ) ?? countries[0]
      );
    } else if (phoneNumber) {
      setNewPhoneNumber(sanitizePhoneNumber(phoneNumber));
    } else {
      setCountry(
        countries.find((country: Country) => country.regionCode === 'US')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect((): void => {
    if (country && newPhoneNumber.length > country.length) {
      setNewPhoneNumber(newPhoneNumber.slice(0, country.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    if (visible) {
      setStep(1);
    }
  }, [visible]);

  return (
    <KitModalV2
      anchorBottom={isMobileDevice}
      avoidKeyboard={true}
      isVisible={visible}
      modalStyle={styles.modalStyle}
      backdropStyle={styles.backdropStyle}
      onClose={onClose}
      coverScreen
      scrollViewProps={{
        keyboardShouldPersistTaps: 'handled',
      }}
    >
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps='handled'
        enableOnAndroid={true}
        showsVerticalScrollIndicator={false}
        enableAutomaticScroll={
          Platform.OS === 'android'
            ? Platform.Version < 31 || Platform.Version > 34
            : false // Android 31 - 34
        }
        extraScrollHeight={Platform.OS === 'android' ? insetsBottom : 0}
      >
        {renderContent()}
      </KeyboardAwareScrollView>
    </KitModalV2>
  );
};

const styles = StyleSheet.create({
  modalStyle: {
    width: '100%',
    maxWidth: 600,
    paddingTop: 0,
    paddingHorizontal: 0,
    paddingBottom: 0,
    alignSelf: 'center',
  },
  backdropStyle: {
    alignItems: undefined,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  container: {
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.xl,
  },
  contentContainer: {
    marginTop: Spacing.xl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
  cell: {
    backgroundColor: Colors.N75,
    borderRadius: BorderRadius.m,
    color: Colors.N900,
    fontSize: 32,
    height: 54,
    overflow: 'hidden',
    paddingTop: Platform.OS === 'ios' ? 8 : 5,
    textAlign: 'center',
    width: 44,
  },
  focusCell: { color: Colors.brand },
});

export default ChangePhoneNumberModal;
